import { colorDifference, COLOR_DIFFERENCE_METHOD } from './colorDifference';
import { getContrastRatio } from './colorAnalysis';

const ORIGIN = 'https://color-match.io';
export const CONTRAST_REPORT_VERSION = 'contrast-report-v1';
export const CONTRAST_METHOD = {
  version: 'wcag22-srgb-v1',
  name: 'WCAG 2.2 relative luminance contrast',
  input: 'Opaque 8-bit sRGB',
  transferBreakpoint: 0.04045,
  passRule: 'Unrounded contrast ratio >= target ratio',
  source: 'https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html',
} as const;

export interface ContrastPairInput {
  label: string;
  foreground: string;
  background: string;
  targetRatio: number;
  adjustedForeground?: string;
  relationship?: 'text-background' | 'text-distinction';
}

export interface ContrastReportPair {
  label: string;
  background: string;
  targetRatio: number;
  relationship: 'text-background' | 'text-distinction';
  original: { foreground: string; ratio: number; passes: boolean };
  adjusted?: { foreground: string; ratio: number; passes: boolean; deltaE00: number; backgroundFixed: true };
}

export interface ContrastReport {
  schemaVersion: typeof CONTRAST_REPORT_VERSION;
  title: string;
  sourceUrl: string;
  methods: { contrast: typeof CONTRAST_METHOD; difference: typeof COLOR_DIFFERENCE_METHOD; correction: string };
  pairs: ContrastReportPair[];
  notes: string[];
}

function normalizeHex(hex: string): string {
  const raw = hex.trim().replace(/^#/, '');
  const expanded = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) throw new TypeError('Report colors must be opaque sRGB HEX values');
  return '#' + expanded.toUpperCase();
}

/** Only explicit color-tool state is carried into report URLs. */
export function reportSourceUrl(sourcePath: string): string {
  const url = new URL(sourcePath, ORIGIN);
  if (url.origin !== ORIGIN || url.username || url.password) throw new TypeError('Report source must be a Color Match URL');
  const clean = new URL(ORIGIN);
  clean.pathname = url.pathname;
  for (const [key, value] of url.searchParams) {
    if (['fg', 'bg', 'link', 'text', 'body', 'hover', 'focus', 'visited'].includes(key) && /^#?[0-9a-f]{6}$/i.test(value)) {
      clean.searchParams.set(key, value.replace(/^#/, '').toUpperCase());
    } else if (key === 'target' && ['3', '4.5', '7'].includes(value)) clean.searchParams.set(key, value);
    else if (key === 'underline' && ['always', 'interaction', 'none'].includes(value)) clean.searchParams.set(key, value);
  }
  return clean.href;
}

export function createContrastReport(input: { title?: string; sourcePath: string; pairs: ContrastPairInput[]; notes?: string[] }): ContrastReport {
  if (!input.pairs.length) throw new TypeError('A contrast report needs at least one tested pair');
  return {
    schemaVersion: CONTRAST_REPORT_VERSION,
    title: input.title || 'Color Match contrast report',
    sourceUrl: reportSourceUrl(input.sourcePath),
    methods: {
      contrast: CONTRAST_METHOD,
      difference: COLOR_DIFFERENCE_METHOD,
      correction: input.pairs.some(pair => pair.adjustedForeground) ? 'oklch-lightness-search-v1: foreground only; final 8-bit sRGB result rechecked; no claim of a globally minimal ΔE correction' : 'No foreground correction included',
    },
    pairs: input.pairs.map(pair => {
      if (!Number.isFinite(pair.targetRatio) || pair.targetRatio < 1 || pair.targetRatio > 21) throw new RangeError('Target ratio must be between 1 and 21');
      const foreground = normalizeHex(pair.foreground), background = normalizeHex(pair.background);
      const ratio = getContrastRatio(foreground, background);
      const result: ContrastReportPair = { label: pair.label, background, targetRatio: pair.targetRatio, relationship: pair.relationship || 'text-background', original: { foreground, ratio, passes: ratio >= pair.targetRatio } };
      if (pair.adjustedForeground) {
        const adjustedForeground = normalizeHex(pair.adjustedForeground);
        const adjustedRatio = getContrastRatio(adjustedForeground, background);
        result.adjusted = { foreground: adjustedForeground, ratio: adjustedRatio, passes: adjustedRatio >= pair.targetRatio, deltaE00: colorDifference(foreground, adjustedForeground), backgroundFixed: true };
      }
      return result;
    }),
    notes: [
      'Pass/fail evaluates each stated contrast target using the raw ratio; it does not certify the whole interface.',
      'Opaque, solid sRGB colors only. Check compositing, text size and weight, interaction states, and non-color cues in context.',
      'ΔE00 describes color difference, not accessibility or a similarity percentage. Visibility depends on viewing conditions.',
      ...(input.notes || []),
    ],
  };
}

const markdownText = (value: string) => value.replace(/[\\`*_{}\[\]<>|]/g, '\\$&').replace(/[\r\n]+/g, ' ');
const htmlText = (value: string) => value.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]!));
const status = (passes: boolean) => passes ? 'Pass' : 'Fail';

export function contrastReportMarkdown(report: ContrastReport): string {
  const rows = report.pairs.flatMap(pair => {
    const result = [`| ${markdownText(pair.label)} — original | ${pair.original.foreground} | ${pair.background} | ${pair.original.ratio}:1 | ${pair.targetRatio}:1 | ${status(pair.original.passes)} | — |`];
    if (pair.adjusted) result.push(`| ${markdownText(pair.label)} — adjusted | ${pair.adjusted.foreground} | ${pair.background} | ${pair.adjusted.ratio}:1 | ${pair.targetRatio}:1 | ${status(pair.adjusted.passes)} | ${pair.adjusted.deltaE00} |`);
    return result;
  });
  return [
    '# ' + markdownText(report.title), '', '[Reproduce this result](' + report.sourceUrl + ')', '',
    '| Pair | Foreground / link color | Background / adjacent text color | Raw contrast | Target | Result | ΔE00 from original |',
    '| --- | --- | --- | --- | --- | --- | --- |', ...rows, '',
    '## Calculation conditions', '',
    `- Report: ${report.schemaVersion}; contrast: ${report.methods.contrast.version}.`,
    `- ${report.methods.contrast.name}. ${report.methods.contrast.passRule}.`,
    `- Difference: ${report.methods.difference.version}; CIE Lab D50, CIE 1931 2° observer; Bradford D65 → D50; kL = kC = kH = 1.`,
    `- Correction: ${report.methods.correction}.`, '',
    ...report.notes.map(note => '- ' + markdownText(note)),
    ...report.pairs.filter(pair => pair.relationship === 'text-distinction').map(pair => '- ' + markdownText(pair.label) + ': compares adjacent text colors; the second color is surrounding text, not the page background.'), '',
    '[WCAG method](' + report.methods.contrast.source + ') · [Color conversions](' + report.methods.difference.sources[0] + ') · [CIEDE2000 reference](' + report.methods.difference.sources[1] + ')', '',
  ].join('\n');
}

export function contrastReportHtml(report: ContrastReport): string {
  const rows = report.pairs.flatMap(pair => {
    const row = (label: string, foreground: string, ratio: number, passes: boolean, difference?: number) =>
      `<tr><th scope="row">${htmlText(label)}</th><td>${foreground}</td><td>${pair.background}</td><td>${ratio}:1</td><td>${pair.targetRatio}:1</td><td>${status(passes)}</td><td>${difference ?? '—'}</td></tr>`;
    const result = [row(pair.label + ' — original', pair.original.foreground, pair.original.ratio, pair.original.passes)];
    if (pair.adjusted) result.push(row(pair.label + ' — adjusted', pair.adjusted.foreground, pair.adjusted.ratio, pair.adjusted.passes, pair.adjusted.deltaE00));
    return result;
  }).join('\n');
  const previews = report.pairs.map(pair => pair.relationship === 'text-distinction'
    ? `<section><h2>${htmlText(pair.label)}</h2><p>Adjacent text-color comparison: link ${pair.original.foreground} against surrounding text ${pair.background}. The second color is surrounding text, not the page background; no overlay preview is shown.</p></section>`
    : `<section><h2>${htmlText(pair.label)}</h2><div class="previews"><div><h3>Original · ${pair.original.foreground}</h3><p class="sample" style="color:${pair.original.foreground};background:${pair.background}">The quick brown fox jumps over the lazy dog.</p></div>${pair.adjusted ? `<div><h3>Adjusted · ${pair.adjusted.foreground}</h3><p class="sample" style="color:${pair.adjusted.foreground};background:${pair.background}">The quick brown fox jumps over the lazy dog.</p></div>` : ''}</div></section>`).join('\n');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="robots" content="noindex"><title>${htmlText(report.title)}</title>
<style>body{font:16px/1.6 system-ui,sans-serif;color:#202531;background:#f7f8fa;max-width:1120px;margin:0 auto;padding:32px 20px}h1{font-size:28px}h2{font-size:22px}h3{font-size:16px}a{color:#4338ca;overflow-wrap:anywhere}section,.table-wrap{margin:24px 0;padding:20px;background:#fff;border:1px solid #e2e5eb;border-radius:16px}.table-wrap{overflow-x:auto}table{border-collapse:collapse;min-width:760px;width:100%;font-size:14px}th,td{text-align:left;padding:12px;border-bottom:1px solid #e2e5eb}td{font-variant-numeric:tabular-nums}.previews{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px}.sample{padding:24px;border:1px solid #e2e5eb;border-radius:10px;font-size:16px;font-weight:400}.method{font-size:14px;overflow-wrap:anywhere}@media print{body{background:#fff;padding:0}.table-wrap{overflow:visible;padding:0}table{min-width:0;font-size:10px}th,td{padding:6px;overflow-wrap:anywhere}.sample{print-color-adjust:exact;-webkit-print-color-adjust:exact}}</style></head>
<body><main><h1>${htmlText(report.title)}</h1><p><a href="${htmlText(report.sourceUrl)}">Reproduce this result in Color Match</a></p><p>${htmlText(report.sourceUrl)}</p>
<div class="table-wrap"><table><caption>Raw contrast results; all pass flags use unrounded values</caption><thead><tr><th scope="col">Pair</th><th scope="col">Foreground / link color</th><th scope="col">Background / adjacent text color</th><th scope="col">Raw contrast</th><th scope="col">Target</th><th scope="col">Result</th><th scope="col">ΔE00 from original</th></tr></thead><tbody>${rows}</tbody></table></div>
${previews}<section class="method"><h2>Calculation conditions</h2><p>Report: ${report.schemaVersion}. Contrast: ${report.methods.contrast.version}; opaque 8-bit sRGB; transfer breakpoint 0.04045; raw ratio ≥ target.</p><p>Difference: ${report.methods.difference.version}; CIE Lab D50; CIE 1931 2° observer; Bradford D65 → D50; kL = kC = kH = 1.</p><p>Correction: ${htmlText(report.methods.correction)}.</p><ul>${report.notes.map(note => '<li>' + htmlText(note) + '</li>').join('')}</ul><p><a href="${report.methods.contrast.source}">WCAG method</a> · <a href="${report.methods.difference.sources[0]}">Color conversions</a> · <a href="${report.methods.difference.sources[1]}">CIEDE2000 reference</a></p></section></main></body></html>`;
}
